import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import Button from "../ui/Button";
import "../ui/rc-ui.scss";

/**
 * Shared tabs + actions bar with Orders-style full-width divider.
 *
 * tabs: [{ label, to, exact?, icon?, match? }]
 * actions: [{ label, to?, onClick?, variant?, size? }] | ReactNode
 */
const SubTopbar = ({
  tabs = [],
  actions,
  activePath,
  className = "",
  children,
}) => {
  const { pathname } = useLocation();

  const isTabActive = (tab) => {
    if (activePath != null) {
      return tab.to === activePath || tab.match?.(activePath);
    }
    if (tab.exact) return pathname === tab.to;
    if (tab.match) return tab.match(pathname);
    return pathname === tab.to || pathname.startsWith(`${tab.to}/`);
  };

  const renderActions = () => {
    if (actions == null) return null;
    if (!Array.isArray(actions)) {
      return <div className="rc-ui-subtopbar__actions">{actions}</div>;
    }
    if (actions.length === 0) return null;
    return (
      <div className="rc-ui-subtopbar__actions">
        {actions.map((action, i) => {
          if (React.isValidElement(action)) {
            return <React.Fragment key={action.key || i}>{action}</React.Fragment>;
          }
          const {
            label,
            to,
            onClick,
            variant = "primary",
            size = "md",
            key,
            ...rest
          } = action;
          return (
            <Button
              key={key || to || label || i}
              to={to}
              onClick={onClick}
              variant={variant}
              size={size}
              {...rest}
            >
              {label}
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={["rc-ui-subtopbar", className].filter(Boolean).join(" ")}>
      {tabs.length > 0 && (
        <nav className="rc-ui-subtopbar__tabs" aria-label="Section tabs">
          {tabs.map((tab) => {
            const active = isTabActive(tab);
            return (
              <NavLink
                key={tab.to || tab.label}
                to={tab.to}
                exact={!!tab.exact}
                className={`rc-ui-subtopbar__tab${active ? " rc-ui-subtopbar__tab--active" : ""}`}
                activeClassName="rc-ui-subtopbar__tab--active"
              >
                {tab.icon}
                {tab.label}
              </NavLink>
            );
          })}
        </nav>
      )}
      <div className="rc-ui-subtopbar__spacer" aria-hidden="true" />
      {renderActions()}
      {children}
    </div>
  );
};

export default SubTopbar;
